import { DUREE_MAX_ACCES_HEURES, type StatutAccesTemporaire } from '@admin-pmvb/core'
import { queryOptions, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query'
import { Link, useRouterState } from '@tanstack/react-router'
import { useState } from 'react'
import { api, ErreurApi, lire, requeteMoi } from '../api'
import { formaterInstant } from './Historique'
import { Icone } from './Icone'

// Accès temporaires à un espace privé (invariant I4) : la liste de la personne connectée (ceux
// de ses espaces, ceux qu'elle a demandés), le bandeau qui dit à la titulaire qu'une demande
// attend ou qu'un accès court, et le bouton de demande d'un administrateur (vue des espaces
// privés). Aucune donnée de l'espace ici : nom, demandeur, motif, échéance.

export const requeteAccesTemporaires = queryOptions({
  queryKey: ['acces-temporaires'],
  queryFn: () => lire(api['acces-temporaires'].$get()),
  // Une demande arrive aussi par notification : relue chaque minute, et au retour sur l'appli.
  refetchInterval: 60_000,
})

export type ListeAcces = Awaited<ReturnType<NonNullable<typeof requeteAccesTemporaires.queryFn>>>
export type AccesRecu = ListeAcces['recues'][number]

export const LIBELLES_STATUT_ACCES: Record<StatutAccesTemporaire, string> = {
  demande: 'En attente de réponse',
  accorde: 'En cours',
  refuse: 'Refusé',
  revoque: 'Révoqué',
  expire: 'Terminé',
}

/** Durées proposées à l'accord, en heures (48 h au plus). */
export const DUREES_ACCES = [1, 4, 24, DUREE_MAX_ACCES_HEURES] as const

export function libelleDuree(heures: number): string {
  return heures < 24 ? `${heures} h` : heures === 24 ? '24 h' : `${heures / 24} jours`
}

const prenom = (nom: string) => nom.split(' ')[0] ?? nom

/**
 * Bandeau chez la titulaire, sur toutes les pages sauf celle des accès : une demande attend sa
 * réponse, ou un administrateur consulte son espace en ce moment. Rien sinon. Il se replie en
 * une clé avec un compteur, et se rouvre de lui-même quand une demande ou un accès change.
 */
export function BandeauAccesTemporaires() {
  // Monté chez une titulaire d'espace privé seulement, administratrice comprise : ailleurs, ni
  // bandeau ni requête périodique (I6).
  const titulaire = useSuspenseQuery(requeteMoi).data.titulaireEspacePrive
  return titulaire ? <BandeauTitulaire /> : null
}

function BandeauTitulaire() {
  const acces = useQuery(requeteAccesTemporaires)
  const surLaPage = useRouterState({
    select: (s) => s.location.pathname === '/acces-temporaires',
  })
  const [replieSur, setReplieSur] = useState<string | null>(null)
  const recues = acces.data?.recues ?? []
  const enAttente = recues.filter((a) => a.statut === 'demande')
  const enCours = recues.filter((a) => a.statut === 'accorde')
  if (surLaPage || (enAttente.length === 0 && enCours.length === 0)) return null
  const signature = [...enAttente, ...enCours].map((a) => `${a.id}:${a.statut}`).join(',')
  const nombre = enAttente.length + enCours.length
  if (replieSur === signature)
    return (
      <aside className="bandeau-acces bandeau-acces-replie" aria-label="Accès temporaires">
        <button
          type="button"
          className="bandeau-acces-bouton"
          onClick={() => setReplieSur(null)}
          title="Afficher les accès temporaires"
          aria-label={`Accès temporaires : ${nombre}, afficher`}
        >
          <Icone nom="key" taille={18} />
          <span className="compteur">{nombre}</span>
        </button>
      </aside>
    )
  const premier = enCours[0] ?? enAttente[0]!
  const texte =
    enCours.length > 0
      ? `${prenom(premier.beneficiaire)} consulte « ${premier.espace} » en lecture seule jusqu’au ${premier.fin ? formaterInstant(String(premier.fin)) : ''}.`
      : enAttente.length === 1
        ? `${prenom(premier.beneficiaire)} demande à consulter « ${premier.espace} ».`
        : `${enAttente.length} demandes d’accès à votre espace attendent votre réponse.`
  return (
    <aside className="bandeau-acces" role="status" aria-label="Accès temporaires">
      <Icone nom={enCours.length > 0 ? 'key' : 'shield'} taille={18} />
      <span className="bandeau-acces-texte">{texte}</span>
      <Link to="/acces-temporaires" className="bandeau-acces-lien">
        {enCours.length > 0 ? 'Gérer' : 'Répondre'}
      </Link>
      <button
        type="button"
        className="bandeau-acces-bouton"
        onClick={() => setReplieSur(signature)}
        title="Replier"
        aria-label="Replier le bandeau"
      >
        <Icone nom="close" taille={16} />
      </button>
    </aside>
  )
}

/**
 * Bouton « Demander un accès » d'un administrateur pour un espace privé dont il n'est pas
 * titulaire (vue des espaces privés) ; montre où en est sa dernière demande.
 */
export function DemanderAcces({ espace }: { espace: { id: string; nom: string } }) {
  const client = useQueryClient()
  const acces = useQuery(requeteAccesTemporaires)
  const [ouvert, setOuvert] = useState(false)
  const [motif, setMotif] = useState('')
  const [erreur, setErreur] = useState<string | null>(null)
  const [envoi, setEnvoi] = useState(false)
  const derniere = acces.data?.envoyees.find((a) => a.entiteId === espace.id)

  if (derniere?.statut === 'demande')
    return <p className="discret">Demande envoyée, en attente de la titulaire.</p>
  if (derniere?.statut === 'accorde')
    return (
      <p className="discret">
        Accès en lecture seule jusqu’au {derniere.fin ? formaterInstant(String(derniere.fin)) : ''}.{' '}
        <Link to="/entites/$id" params={{ id: espace.id }}>
          Ouvrir
        </Link>
      </p>
    )
  if (!ouvert)
    return (
      <button type="button" className="bouton-discret" onClick={() => setOuvert(true)}>
        Demander un accès
      </button>
    )
  return (
    <form
      className="formulaire"
      onSubmit={async (e) => {
        e.preventDefault()
        setEnvoi(true)
        setErreur(null)
        try {
          await lire(
            api['espaces-prives'][':id']['demandes-acces'].$post({
              param: { id: espace.id },
              json: { motif },
            }),
          )
          setOuvert(false)
          setMotif('')
          await client.invalidateQueries({ queryKey: ['acces-temporaires'] })
        } catch (x) {
          setErreur(x instanceof ErreurApi ? x.message : 'Demande impossible.')
        } finally {
          setEnvoi(false)
        }
      }}
    >
      <label className="champ">
        <span>Motif, lu par la titulaire</span>
        <textarea
          value={motif}
          onChange={(e) => setMotif(e.target.value)}
          maxLength={500}
          rows={2}
          required
        />
      </label>
      <p className="discret">
        La titulaire de « {espace.nom} » est prévenue. Si elle accepte, vous lisez son espace sans
        rien y modifier, {DUREE_MAX_ACCES_HEURES} h au plus ; elle peut y mettre fin à tout moment.
      </p>
      {erreur && <p className="alerte">{erreur}</p>}
      <div className="boutons">
        <button type="submit" className="bouton" disabled={envoi || motif.trim() === ''}>
          Envoyer la demande
        </button>
        <button type="button" className="bouton-discret" onClick={() => setOuvert(false)}>
          Annuler
        </button>
      </div>
    </form>
  )
}
